import React from "react";

import { Spinner, Title } from "@amphore-dev/amphore-lib";
import { format as formatDate } from "date-fns";
import { enUS } from "date-fns/locale";
import { t } from "i18next";

import { useActiveProject } from "@/hooks/useActiveProject";
import { useUserSettings } from "@/hooks/useUserSettings";

import { getDeltaColor } from "@/utils/UAnalytics";
import { convertToDecimalHours, convertToTime } from "@/utils/UDates";
import { cn } from "@/utils/cn";

import { TDurationResult } from "@/types";
import { TDayName } from "@/types";

export interface IDayHeaderProps {
	date: string;
	nbrTasks?: number;
	duration?: TDurationResult;
	format?: string;
	isLoading?: boolean;
	className?: string;
}

export const DayHeader: React.FC<IDayHeaderProps> = ({
	date,
	nbrTasks = 0,
	duration = { duration: "0:0", dateFrom: null },
	format = t("dates.dayTitleFull"),
	isLoading,
	className = "",
}) => {
	const { project: activeProject } = useActiveProject();
	// The hours a day owes are the person's, not the view's (phase 4).
	const { settings } = useUserSettings();

	const dayName = formatDate(date, "EEEE", {
		locale: enUS,
	}).toLowerCase();

	const isFuture = new Date(date) > new Date();

	const daySettings = settings?.workDays[dayName as TDayName];

	const requiredHours = daySettings?.isWorkingDay
		? daySettings?.hours || settings?.hoursPerDay || "0:0"
		: "0:0";

	const expectedHoursDelta = parseFloat(
		(
			convertToDecimalHours(duration.duration) -
			convertToDecimalHours(requiredHours)
		).toFixed(4)
	);

	return (
		<div
			className={cn([
				"flex items-center justify-between px-2 md:px-4",
				className,
			])}
		>
			<div className={cn([isLoading && "row items-center gap-2"])}>
				<Title size="h4" className="capitalize">
					{formatDate(date, format)}
				</Title>
				{!isLoading ? (
					<div className="row items-center gap-2">
						<div className="text-neutral-400">
							{t(
								isFuture
									? "tasks.futureCount"
									: "tasks.workedOn",
								{
									count: nbrTasks,
									duration: duration.duration,
								}
							)}
						</div>
						{!isFuture &&
							!activeProject &&
							(daySettings?.isWorkingDay || !!nbrTasks) && (
								<span
									className={cn([
										"text-sm",
										getDeltaColor(expectedHoursDelta),
										"bg-transparent",
									])}
								>
									{expectedHoursDelta > 0 ? "+" : ""}
									{convertToTime(expectedHoursDelta)}
								</span>
							)}
					</div>
				) : (
					<Spinner size="sm" />
				)}
			</div>
			<div></div>
		</div>
	);
};
